/**
 * The NavLog's columns, as data — `docs/design/aether-ux.md` §9.2 in one array.
 *
 * A column knows four things and nothing else: which store field it shows for a
 * given row, whether the pilot may edit it HERE, how wide it is, and how early
 * it disappears when the inspector is narrow. Everything a column does is
 * therefore visible in one screen of code, and adding one is one line.
 *
 * The editing split (§9.1) lives in the `editable` flag: the NavLog edits the
 * INPUTS of the flight — the checkpoint, the altitude, the TAS, the wind, the
 * distance — and shows what the calculator makes of them. Overriding a computed
 * value is a deliberate act and lives in the Formular, which is the document of
 * record and where every cell is editable.
 */

import { legField, type LegNumber } from '../../fields';
import type { NavRow } from './model';

export type ColumnKey =
  | 'idx' | 'cp' | 'freq' | 'safealt' | 'alt' | 'tas' | 'wind' | 'tc' | 'var' | 'wca'
  | 'mh' | 'mc' | 'dist' | 'rest_dist' | 'gs' | 'time' | 'rest_time' | 'fuel'
  | 'eto' | 'ato' | 'remark';

/**
 * How early a column goes: 0 survives every width, 2 only appears when the pane
 * is wide enough for the whole sheet. The rank-0 set is §9.2's condensed one —
 * where you are, how high, how fast, which heading, how far, how long.
 */
export type Rank = 0 | 1 | 2;

export interface Column {
  key: ColumnKey;
  /** catalogue key of the header */
  head: string;
  /** catalogue key of the full name, where the header is an abbreviation */
  full?: string;
  /** the column's unit, printed once under the header; symbols, not prose */
  unit?: string;
  /** text columns are left-aligned; every number is right-aligned and mono */
  text?: boolean;
  /** the pilot may type here (in the rows where the column has a cell at all) */
  editable?: boolean;
  rank: Rank;
  /** px; the table is fixed-layout so the columns do not dance while typing */
  w: number;
}

/**
 * The order here IS the order on screen, and the first columns are the ones a
 * pilot flies by. The sheet's own order put SH, Höhe and TAS ahead of NM, W/V,
 * GS, ETE and ETO, so a 387 px inspector spent every visible pixel on numbers
 * that do not change in flight and pushed the five that do off the right edge.
 * Planning inputs (SH, Höhe/FL, TAS) follow them: still rank 0, still typed
 * here (§9.1), one short scroll away.
 */
export const COLUMNS: readonly Column[] = [
  { key: 'idx', head: 'th.leg', rank: 0, w: 40, text: true },
  { key: 'cp', head: 'th.fromto', rank: 0, w: 150, text: true, editable: true },
  { key: 'dist', head: 'th.dist', full: 'leg.dist', rank: 0, w: 48, editable: true },
  { key: 'wind', head: 'th.wind', full: 'leg.wind', rank: 0, w: 78, editable: true },
  { key: 'gs', head: 'th.gs', full: 'leg.gs', unit: 'kt', rank: 0, w: 54 },
  { key: 'time', head: 'th.time', full: 'leg.time', rank: 0, w: 44 },
  { key: 'eto', head: 'th.eto', full: 'cp.eto', rank: 0, w: 54, editable: true },
  { key: 'mh', head: 'th.mh', full: 'leg.mh', rank: 0, w: 56 },
  { key: 'safealt', head: 'th.sh', full: 'leg.safealt', unit: 'ft', rank: 0, w: 64, editable: true },
  { key: 'alt', head: 'th.alt', full: 'leg.alt', rank: 0, w: 76, editable: true },
  { key: 'tas', head: 'th.tas', full: 'leg.tas', unit: 'kt', rank: 0, w: 56, editable: true },
  { key: 'rest_dist', head: 'th.rest_dist', full: 'leg.rest_dist', unit: 'NM', rank: 1, w: 62 },
  { key: 'rest_time', head: 'th.rest_time', full: 'leg.rest_time', unit: 'min', rank: 1, w: 66 },
  { key: 'fuel', head: 'th.fuel', unit: 'l', rank: 1, w: 84 },
  { key: 'tc', head: 'th.tc', full: 'leg.tc', rank: 1, w: 46, editable: true },
  { key: 'remark', head: 'th.remark', full: 'leg.remark', rank: 1, w: 140, text: true, editable: true },
  { key: 'freq', head: 'th.vor', full: 'leg.freq', rank: 2, w: 72, editable: true },
  { key: 'var', head: 'th.var', full: 'leg.var', rank: 2, w: 46, editable: true },
  { key: 'wca', head: 'th.wca', full: 'leg.wca', rank: 2, w: 46 },
  { key: 'mc', head: 'th.mc', full: 'leg.mc', rank: 2, w: 46 },
  { key: 'ato', head: 'th.ato', full: 'cp.ato', rank: 2, w: 54, editable: true },
];

/** How wide a set of columns is — the table is sized in px, never guessed. */
export const widthOf = (cols: readonly Column[]): number => cols.reduce((n, c) => n + c.w, 0);

/** The two columns that stay put while the rest scroll sideways (§9.4). */
export const STICKY_KEYS: readonly ColumnKey[] = ['idx', 'cp'];

const byKey = new Map(COLUMNS.map(c => [c.key, c]));

/** A column by key — the checkpoint column is needed outside the table's loop. */
export function column(key: ColumnKey): Column {
  const c = byKey.get(key);
  if (!c) throw new Error('no such NavLog column: ' + key);
  return c;
}

/* ---------------------------------------------------------------- *
 * Which columns fit
 * ---------------------------------------------------------------- */

/*
 * These are PANE widths, not window widths — the inspector is what the table
 * lives in, and it is 420 px wide (340 on an iPad), 560+ while the NavLog tab
 * is open on a desktop. Thresholds set against the window (1180/560) meant rank
 * 1 and rank 2 could never be reached inside the inspector at any screen size,
 * so GS, Rest NM, Rest min and Bemerkung existed only in the row expander.
 */

/** Above this the whole sheet fits, all twenty-one columns. */
export const FULL_PX = 860;
/** Above this the middle set fits: the totals-carrying columns come back. */
export const STANDARD_PX = 500;

export function rankFor(width: number): Rank {
  if (width >= FULL_PX) return 2;
  return width >= STANDARD_PX ? 1 : 0;
}

/**
 * The columns a pane this wide shows. `fuel` is dropped altogether when the
 * plan has no consumption to divide — a column of blanks teaches nothing, and
 * the pilot has one fewer thing to scroll past.
 */
export function visibleColumns(width: number, showFuel: boolean): Column[] {
  const rank = rankFor(width);
  return COLUMNS.filter(c => c.rank <= rank && (c.key !== 'fuel' || showFuel));
}

/** What the row expander reveals: everything this width dropped. */
export function hiddenColumns(width: number, showFuel: boolean): Column[] {
  const rank = rankFor(width);
  return COLUMNS.filter(c => c.rank > rank && c.key !== 'idx' && (c.key !== 'fuel' || showFuel));
}

/* ---------------------------------------------------------------- *
 * Which field a cell shows
 * ---------------------------------------------------------------- */

export type CellSpec =
  /** this row has no such cell — the alternate has no ETO, the total has no wind */
  | { kind: 'none' }
  /** the row's own name, in the checkpoint column: „Gesamt" */
  | { kind: 'label' }
  | { kind: 'field'; id: string; editable: boolean }
  /** the leg's two ends: `from` is the previous row's checkpoint and read-only here */
  | { kind: 'pair'; from: string | null; to: string }
  /** litres, computed from the flow and this row's minutes */
  | { kind: 'fuel'; time: string };

const NONE: CellSpec = { kind: 'none' };

/** Route-row keys a leg and the alternate both carry. */
const ROW_KEYS: Partial<Record<ColumnKey, string>> = {
  safealt: 'safealt', alt: 'alt', tas: 'tas', wind: 'wind', tc: 'tc', var: 'var',
  wca: 'wca', mh: 'mh', mc: 'mc', dist: 'dist', rest_dist: 'rest_dist', gs: 'gs',
  time: 'time', rest_time: 'rest_time', remark: 'remark',
};

/** The alternate is one aerodrome, not a pair of waypoints: it has no Rest, no ETO/ATO. */
const ALT_DROPS: readonly ColumnKey[] = ['rest_dist', 'rest_time', 'eto', 'ato'];

/** What the totals row carries, and where. */
const TOTAL_FIELDS: Partial<Record<ColumnKey, string>> = {
  dist: 'total_dist', time: 'total_time', eto: 'eta', fuel: 'fuel_reise_l',
};

/**
 * The cell a column has in a row.
 *
 * The checkpoint column is the one that is not a single field: leg `j` runs from
 * `cp{j}` to `cp{j+1}`, so its "Nach" is the editable half and its "Von" is the
 * row above's "Nach" — shown, because a leg without both ends is not a leg, and
 * read-only, because one field with two carets is a field that fights itself.
 */
export function cellFor(col: Column, row: NavRow): CellSpec {
  if (col.key === 'idx') return NONE;

  if (row.kind === 'dep') {
    if (col.key === 'cp') return { kind: 'pair', from: null, to: 'cp1_name' };
    if (col.key === 'freq') return { kind: 'field', id: 'cp1_freq', editable: true };
    return NONE;
  }

  if (row.kind === 'total') {
    if (col.key === 'cp') return { kind: 'label' };
    const id = TOTAL_FIELDS[col.key];
    return id ? { kind: 'field', id, editable: false } : NONE;
  }

  const j = row.row;
  if (j === null) return NONE;

  if (row.kind === 'alt') {
    if (col.key === 'cp') return { kind: 'pair', from: null, to: 'alt_name' };
    if (ALT_DROPS.includes(col.key)) return NONE;
    if (col.key === 'freq') return { kind: 'field', id: 'alt_freq', editable: true };
    if (col.key === 'fuel') return { kind: 'fuel', time: 'alt_time' };
    const k = ROW_KEYS[col.key];
    return k ? { kind: 'field', id: legField('alt', k), editable: col.editable === true } : NONE;
  }

  const leg = j as LegNumber;
  if (col.key === 'cp') return { kind: 'pair', from: 'cp' + leg + '_name', to: 'cp' + (leg + 1) + '_name' };
  if (col.key === 'freq') return { kind: 'field', id: 'cp' + (leg + 1) + '_freq', editable: true };
  if (col.key === 'eto') return { kind: 'field', id: 'cp' + (leg + 1) + '_eto', editable: true };
  if (col.key === 'ato') return { kind: 'field', id: 'cp' + (leg + 1) + '_ato', editable: true };
  if (col.key === 'fuel') return { kind: 'fuel', time: legField(leg, 'time') };
  const k = ROW_KEYS[col.key];
  return k ? { kind: 'field', id: legField(leg, k), editable: col.editable === true } : NONE;
}
